import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMixingAndBakingContext, MB_SCREENS } from '../context/MixingAndBakingContext';
import type { MBCycleRecord } from '../context/MixingAndBakingContext';
import moment from 'moment';

const SummaryReporting: React.FC = () => {
    const { state, dispatch } = useMixingAndBakingContext();
    const navigate = useNavigate();

    // Filters
    const [selectedLine, setSelectedLine] = useState<string>("All");
    const [selectedShift, setSelectedShift] = useState<string>("All");
    const [selectedProduct, setSelectedProduct] = useState<string>("All");
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [selectedCategory, setSelectedCategory] = useState<string>("All");

    // All available cycles from state
    const allCycles: MBCycleRecord[] = useMemo(() => {
        const list = [...state.completedCycles];
        if (state.activeCycleRecord && !list.some(c => c.cycle === state.activeCycleRecord.cycle)) {
            list.push(state.activeCycleRecord);
        }
        return list;
    }, [state.completedCycles, state.activeCycleRecord]);

    // Unique options for filters
    const lineOptions = useMemo(() => Array.from(new Set(allCycles.map(c => c.lineNo).filter(Boolean))), [allCycles]);
    const shiftOptions = useMemo(() => Array.from(new Set(allCycles.map(c => c.shift).filter(Boolean))), [allCycles]);
    const productOptions = useMemo(() => Array.from(new Set(allCycles.map(c => c.productName).filter(Boolean))), [allCycles]);

    // Filtered Cycles
    const filteredCycles = useMemo(() => {
        return allCycles.filter(c => {
            if (selectedLine !== "All" && c.lineNo !== selectedLine) return false;
            if (selectedShift !== "All" && c.shift !== selectedShift) return false;
            if (selectedProduct !== "All" && c.productName !== selectedProduct) return false;
            if (searchTerm) {
                const term = searchTerm.toLowerCase();
                const matchBatch = (c.batchNo || "").toLowerCase().includes(term);
                const matchProd = (c.productName || "").toLowerCase().includes(term);
                const matchCycle = (c.cycle || "").toLowerCase().includes(term);
                const matchExec = (c.observedBy || "").toLowerCase().includes(term);
                if (!matchBatch && !matchProd && !matchCycle && !matchExec) return false;
            }
            return true;
        });
    }, [allCycles, selectedLine, selectedShift, selectedProduct, searchTerm]);

    // Deviation helper
    const calcDev = (obsStr: string, stdStr: string) => {
        const obs = parseFloat(obsStr);
        const std = parseFloat(stdStr);
        if (isNaN(obs) || isNaN(std)) return { diff: "-", pct: "-", isDev: false, numDiff: 0 };
        const diff = obs - std;
        const pct = std !== 0 ? (diff / std) * 100 : 0;
        return {
            diff: diff.toFixed(2),
            pct: pct.toFixed(1),
            isDev: Math.abs(diff) > 0.01,
            numDiff: diff
        };
    };

    // Calculate aggregated metrics
    const metrics = useMemo(() => {
        let totalObservations = filteredCycles.length;
        let totalDeviations = 0;
        let sumDoughTemp = 0;
        let countDoughTemp = 0;
        let sumMoisture = 0;
        let countMoisture = 0;

        filteredCycles.forEach(c => {
            const doughObs = parseFloat(c.mixingDough.doughTempObs);
            if (!isNaN(doughObs)) {
                sumDoughTemp += doughObs;
                countDoughTemp++;
            }
            const moistureObs = parseFloat(c.standards.moistureObs);
            if (!isNaN(moistureObs)) {
                sumMoisture += moistureObs;
                countMoisture++;
            }

            // Check key deviation parameters
            const d1 = calcDev(c.rpo.observed, c.rpo.standard);
            const d2 = calcDev(c.solidFat.observed, c.solidFat.standard);
            const d3 = calcDev(c.mixingDough.doughTempObs, c.mixingDough.doughTempStd);
            const d4 = calcDev(c.forming.observedWetWeight, c.forming.standardWetWeight);
            const d5 = calcDev(c.standards.moistureObs, c.standards.moistureStd);

            if (d1.isDev) totalDeviations++;
            if (d2.isDev) totalDeviations++;
            if (d3.isDev) totalDeviations++;
            if (d4.isDev) totalDeviations++;
            if (d5.isDev) totalDeviations++;
        });

        const avgDoughTemp = countDoughTemp > 0 ? (sumDoughTemp / countDoughTemp).toFixed(1) : "-";
        const avgMoisture = countMoisture > 0 ? (sumMoisture / countMoisture).toFixed(2) : "-";

        return {
            totalObservations,
            totalDeviations,
            avgDoughTemp,
            avgMoisture
        };
    }, [filteredCycles]);

    // Export to CSV
    const exportToCSV = () => {
        if (filteredCycles.length === 0) {
            alert("No data available to export.");
            return;
        }

        const headers = [
            "Cycle",
            "Batch No",
            "Product Name",
            "Line",
            "Shift",
            "Date/Time",
            "Observed By",
            "RPO Std",
            "RPO Obs",
            "RPO Dev",
            "Solid Fat Std",
            "Solid Fat Obs",
            "Solid Fat Dev",
            "Dough Temp Std",
            "Dough Temp Obs",
            "Dough Temp Dev",
            "Wet Weight Std",
            "Wet Weight Obs",
            "Wet Weight Dev",
            "Moisture Std",
            "Moisture Obs",
            "Moisture Dev",
            "Top Product Temp",
            "Bottom Product Temp",
            "Mixer Type",
            "Taste"
        ];

        const rows = filteredCycles.map(c => {
            const rpoDev = calcDev(c.rpo.observed, c.rpo.standard).diff;
            const sfDev = calcDev(c.solidFat.observed, c.solidFat.standard).diff;
            const doughDev = calcDev(c.mixingDough.doughTempObs, c.mixingDough.doughTempStd).diff;
            const wetDev = calcDev(c.forming.observedWetWeight, c.forming.standardWetWeight).diff;
            const moistDev = calcDev(c.standards.moistureObs, c.standards.moistureStd).diff;

            return [
                c.cycle,
                c.batchNo,
                c.productName,
                c.lineNo,
                c.shift,
                moment(c.cycleTime).format("YYYY-MM-DD HH:mm"),
                c.observedBy,
                c.rpo.standard,
                c.rpo.observed,
                rpoDev,
                c.solidFat.standard,
                c.solidFat.observed,
                sfDev,
                c.mixingDough.doughTempStd,
                c.mixingDough.doughTempObs,
                doughDev,
                c.forming.standardWetWeight,
                c.forming.observedWetWeight,
                wetDev,
                c.standards.moistureStd,
                c.standards.moistureObs,
                moistDev,
                c.baking.topProductTemp,
                c.baking.bottomProductTemp,
                c.forming.typeofMixer,
                c.baking.taste
            ];
        });

        const csvContent = "data:text/csv;charset=utf-8," +
            [headers.join(","), ...rows.map(e => e.map(val => `"${val || ""}"`).join(","))].join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `Mixing_Baking_Report_${moment().format("YYYYMMDD_HHmm")}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div id="mixingBakingDashboard" className="py-6 px-4">
            {/* Header Bar */}
            <div className="mb-header">
                <div className="mb-header-title">
                    <span style={{ fontSize: '20px' }}>📊</span>
                    <span>Mixing & Baking Summary & Reporting (Rajpura)</span>
                </div>
                <div className="mb-header-meta">
                    <button
                        type="button"
                        className="mb-btn mb-btn-secondary"
                        onClick={() => dispatch({ type: 'SET_SCREEN', payload: MB_SCREENS.CYCLE_ENTRY })}
                    >
                        ← Back to Active Cycle
                    </button>
                    <button
                        type="button"
                        className="mb-btn mb-btn-primary"
                        onClick={exportToCSV}
                    >
                        📥 Export CSV
                    </button>
                    <button
                        type="button"
                        className="mb-btn mb-btn-secondary"
                        onClick={() => window.print()}
                    >
                        🖨️ Print Report
                    </button>
                    <button
                        type="button"
                        className="mb-btn mb-btn-secondary"
                        onClick={() => navigate('/home')}
                    >
                        Dashboard Home
                    </button>
                </div>
            </div>

            {/* KPI Cards Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                <div className="mb-card" style={{ padding: '20px', textAlign: 'center', marginBottom: 0 }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
                        Total Observations / Cycles
                    </div>
                    <div style={{ fontSize: '32px', fontWeight: 800, color: '#0f172a', marginTop: '6px' }}>
                        {metrics.totalObservations}
                    </div>
                </div>

                <div className="mb-card" style={{ padding: '20px', textAlign: 'center', marginBottom: 0 }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
                        Avg Dough Temperature
                    </div>
                    <div style={{ fontSize: '32px', fontWeight: 800, color: '#0284c7', marginTop: '6px' }}>
                        {metrics.avgDoughTemp}°C
                    </div>
                </div>

                <div className="mb-card" style={{ padding: '20px', textAlign: 'center', marginBottom: 0 }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
                        Avg Moisture %
                    </div>
                    <div style={{ fontSize: '32px', fontWeight: 800, color: '#16a34a', marginTop: '6px' }}>
                        {metrics.avgMoisture}%
                    </div>
                </div>

                <div className="mb-card" style={{ padding: '20px', textAlign: 'center', marginBottom: 0 }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
                        Parameters with Deviations
                    </div>
                    <div style={{ fontSize: '32px', fontWeight: 800, color: metrics.totalDeviations > 0 ? '#d97706' : '#16a34a', marginTop: '6px' }}>
                        {metrics.totalDeviations}
                    </div>
                </div>
            </div>

            {/* Filter Bar Card */}
            <div className="mb-card" style={{ padding: '18px 20px', marginBottom: '20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '14px', alignItems: 'end' }}>
                    <div>
                        <label className="mb-label">Search Batch / Product</label>
                        <input
                            type="text"
                            className="mb-input"
                            placeholder="Type to filter..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div>
                        <label className="mb-label">Line No</label>
                        <select
                            className="mb-select"
                            value={selectedLine}
                            onChange={(e) => setSelectedLine(e.target.value)}
                        >
                            <option value="All">All Lines</option>
                            {lineOptions.map(l => (
                                <option key={l} value={l}>{l}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="mb-label">Shift</label>
                        <select
                            className="mb-select"
                            value={selectedShift}
                            onChange={(e) => setSelectedShift(e.target.value)}
                        >
                            <option value="All">All Shifts</option>
                            {shiftOptions.map(s => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="mb-label">Product Name</label>
                        <select
                            className="mb-select"
                            value={selectedProduct}
                            onChange={(e) => setSelectedProduct(e.target.value)}
                        >
                            <option value="All">All Products</option>
                            {productOptions.map(p => (
                                <option key={p} value={p}>{p}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="mb-label">Parameter View</label>
                        <select
                            className="mb-select"
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                        >
                            <option value="All">All Parameter Highlights</option>
                            <option value="Ingredients">Ingredient Temperatures</option>
                            <option value="Dough">Mixing Dough & Sponge</option>
                            <option value="Baking">Baking Zones 1–7</option>
                            <option value="Standards">Physical Standards & Moisture</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Comprehensive Report Table */}
            <div className="mb-card">
                <div className="mb-card-header">
                    <h4 className="mb-card-title">
                        <span>📋</span>
                        <span>Product / Line / Day-wise Observation Records ({filteredCycles.length})</span>
                    </h4>
                    <span style={{ fontSize: '12px', color: '#64748b' }}>
                        Calculated Deviation = Observed − Standard
                    </span>
                </div>

                <div className="mb-card-body" style={{ padding: 0 }}>
                    <div className="mb-table-container" style={{ border: 'none', margin: 0 }}>
                        <table className="mb-table">
                            <thead>
                                <tr>
                                    <th>Cycle</th>
                                    <th>Batch No</th>
                                    <th>Product</th>
                                    <th>Line / Shift</th>
                                    <th>Date & Time</th>

                                    {(selectedCategory === "All" || selectedCategory === "Ingredients") && (
                                        <>
                                            <th>RPO (°C)</th>
                                            <th>Solid Fat (°C)</th>
                                            <th>Butter (°C)</th>
                                        </>
                                    )}

                                    {(selectedCategory === "All" || selectedCategory === "Dough") && (
                                        <>
                                            <th>Dough Temp (°C)</th>
                                            <th>Jacket Temp (°C)</th>
                                            <th>Consistency</th>
                                        </>
                                    )}

                                    {(selectedCategory === "All" || selectedCategory === "Baking") && (
                                        <>
                                            <th>Baking Time</th>
                                            <th>Top Prod Temp</th>
                                            <th>Bot Prod Temp</th>
                                        </>
                                    )}

                                    {(selectedCategory === "All" || selectedCategory === "Standards") && (
                                        <>
                                            <th>Wet Weight (g)</th>
                                            <th>Moisture %</th>
                                            <th>Taste</th>
                                        </>
                                    )}

                                    <th>Observed By</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredCycles.length === 0 ? (
                                    <tr>
                                        <td colSpan={14} style={{ textAlign: 'center', padding: '32px', color: '#64748b' }}>
                                            No cycle observation records match the selected filters.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredCycles.map((c) => {
                                        const rpoDev = calcDev(c.rpo.observed, c.rpo.standard);
                                        const sfDev = calcDev(c.solidFat.observed, c.solidFat.standard);
                                        const butterDev = calcDev(c.butter.observed, c.butter.standard);
                                        const doughDev = calcDev(c.mixingDough.doughTempObs, c.mixingDough.doughTempStd);
                                        const wetDev = calcDev(c.forming.observedWetWeight, c.forming.standardWetWeight);
                                        const moistDev = calcDev(c.standards.moistureObs, c.standards.moistureStd);

                                        return (
                                            <tr key={c.cycle + (c.batchNo || "")}>
                                                <td>
                                                    <span className="mb-badge-cycle" style={{ fontSize: '11px' }}>
                                                        {c.cycle}
                                                    </span>
                                                </td>
                                                <td style={{ fontWeight: 700, color: '#0f172a' }}>{c.batchNo || "N/A"}</td>
                                                <td style={{ fontWeight: 600 }}>{c.productName || "Butter Bake"}</td>
                                                <td>{c.lineNo} ({c.shift})</td>
                                                <td style={{ fontSize: '12px', color: '#64748b' }}>
                                                    {moment(c.cycleTime).format("DD-MM-YYYY hh:mm A")}
                                                </td>

                                                {(selectedCategory === "All" || selectedCategory === "Ingredients") && (
                                                    <>
                                                        <td>
                                                            <div>{c.rpo.observed || "-"}</div>
                                                            {rpoDev.isDev && (
                                                                <span className="mb-dev-pill mb-dev-warn" style={{ fontSize: '10px' }}>
                                                                    Dev: {rpoDev.diff}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <div>{c.solidFat.observed || "-"}</div>
                                                            {sfDev.isDev && (
                                                                <span className="mb-dev-pill mb-dev-warn" style={{ fontSize: '10px' }}>
                                                                    Dev: {sfDev.diff}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <div>{c.butter.observed || "-"}</div>
                                                            {butterDev.isDev && (
                                                                <span className="mb-dev-pill mb-dev-warn" style={{ fontSize: '10px' }}>
                                                                    Dev: {butterDev.diff}
                                                                </span>
                                                            )}
                                                        </td>
                                                    </>
                                                )}

                                                {(selectedCategory === "All" || selectedCategory === "Dough") && (
                                                    <>
                                                        <td>
                                                            <div style={{ fontWeight: 600 }}>{c.mixingDough.doughTempObs || "-"}</div>
                                                            {doughDev.isDev && (
                                                                <span className="mb-dev-pill mb-dev-warn" style={{ fontSize: '10px' }}>
                                                                    Dev: {doughDev.diff} ({doughDev.pct}%)
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td>{c.mixingDough.jacketTempObs || "-"}</td>
                                                        <td>{c.mixingDough.doughConsistencyObs || "-"}</td>
                                                    </>
                                                )}

                                                {(selectedCategory === "All" || selectedCategory === "Baking") && (
                                                    <>
                                                        <td>{c.baking.bakingTime || "-"}</td>
                                                        <td style={{ fontWeight: 600, color: '#b45309' }}>{c.baking.topProductTemp || "-"}°C</td>
                                                        <td style={{ fontWeight: 600, color: '#0369a1' }}>{c.baking.bottomProductTemp || "-"}°C</td>
                                                    </>
                                                )}

                                                {(selectedCategory === "All" || selectedCategory === "Standards") && (
                                                    <>
                                                        <td>
                                                            <div>{c.forming.observedWetWeight || "-"}g</div>
                                                            {wetDev.isDev && (
                                                                <span className="mb-dev-pill mb-dev-warn" style={{ fontSize: '10px' }}>
                                                                    Dev: {wetDev.diff}g
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <div style={{ fontWeight: 700 }}>{c.standards.moistureObs || "-"}%</div>
                                                            {moistDev.isDev && (
                                                                <span className="mb-dev-pill mb-dev-warn" style={{ fontSize: '10px' }}>
                                                                    Dev: {moistDev.diff}%
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td>{c.baking.taste || "Normal"}</td>
                                                    </>
                                                )}

                                                <td style={{ fontSize: '12px', color: '#475569' }}>{c.observedBy || "-"}</td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SummaryReporting;
